import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, type ID, type LeaveRequestInput } from '@hrms/shared';
import { api } from '../../../lib/api';
import { todayISO } from '../../../lib/date';
import { errorMessage, fieldErrors } from '../../../lib/errors';
import { useDebounced } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { Alert, Button, Card, Columns, ConfirmDialog, DataTable, DateField, Dot, Grid, Loading, PageHeader, Row, Screen, Select, Stack, StatusBadge, Text, TextField, useTheme } from '../../../ui';
import { WorkflowSheet } from '../components/WorkflowSheet';

export function ApplyLeaveScreen() {
  const { colors } = useTheme();
  const balances = useQuery({ queryKey: ['leave', 'balances', 'me'], queryFn: () => api.leave.balances() });
  const [form, setForm] = useState<LeaveRequestInput>({ leave_type_id: 0, from_date: todayISO(), to_date: todayISO(), from_half: 'full', to_half: 'full', reason: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [wf, setWf] = useState<ID | null>(null);
  const [cancelId, setCancelId] = useState<ID | null>(null);
  const dForm = useDebounced(form, 300);
  const valid = !!dForm.leave_type_id && !!dForm.from_date && !!dForm.to_date && dForm.to_date >= dForm.from_date;
  const preview = useQuery({
    queryKey: ['leave', 'preview', dForm.leave_type_id, dForm.from_date, dForm.to_date, dForm.from_half, dForm.to_half],
    queryFn: () => api.leave.preview({ ...dForm, reason: undefined }),
    enabled: valid,
    retry: false,
    staleTime: 0,
  });
  const requests = useQuery({ queryKey: ['leave', 'requests', 'mine', page], queryFn: () => api.leave.requests({ scope: 'mine', page }) });
  const apply = useMut(() => api.leave.apply(form), {
    invalidate: [['leave'], ['dashboard']],
    success: 'Leave request submitted',
    onSuccess: () => {
      setForm({ ...form, reason: '' });
      setErrs({});
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const cancel = useMut((id: ID) => api.leave.cancel(id), { invalidate: [['leave']], success: 'Leave cancelled', onSuccess: () => setCancelId(null) });
  const set = <K extends keyof LeaveRequestInput>(k: K, v: LeaveRequestInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const p = preview.data;
  const sameDay = form.from_date === form.to_date;

  return (
    <Screen>
      <PageHeader title="Apply leave" />
      <Columns ratio={[2, 3]}>
        <Card title="Leave request">
          <Stack gap={14}>
            <Select
              label="Leave type"
              required
              testID="leave-type"
              value={form.leave_type_id || null}
              onChange={(v) => set('leave_type_id', v)}
              placeholder={balances.isLoading ? 'Loading…' : 'Select leave type…'}
              error={errs.leave_type_id}
              options={(balances.data ?? []).map((b) => ({ value: b.leave_type_id, label: b.leave_type.name, description: `${b.balance} available` }))}
            />
            <Grid min={160} gap={12}>
              <DateField label="From" required value={form.from_date} error={errs.from_date} onChange={(v) => setForm((f) => ({ ...f, from_date: v, to_date: f.to_date < v ? v : f.to_date }))} />
              <DateField label="To" required value={form.to_date} min={form.from_date} error={errs.to_date} onChange={(v) => set('to_date', v)} />
            </Grid>
            <Grid min={160} gap={12}>
              <Select
                label={sameDay ? 'Session' : 'First day'}
                value={form.from_half ?? 'full'}
                onChange={(v) => set('from_half', v)}
                options={[
                  { value: 'full', label: 'Full day' },
                  { value: 'second_half', label: sameDay ? 'Half day (second half)' : 'From second half' },
                ]}
              />
              {!sameDay && (
                <Select
                  label="Last day"
                  value={form.to_half ?? 'full'}
                  onChange={(v) => set('to_half', v)}
                  options={[
                    { value: 'full', label: 'Full day' },
                    { value: 'first_half', label: 'Till first half' },
                  ]}
                />
              )}
            </Grid>
            <TextField label="Reason" multiline value={form.reason ?? ''} onChangeText={(v) => set('reason', v)} error={errs.reason} />
            {preview.isFetching && <Loading inline label="Calculating…" />}
            {preview.isError && <Alert tone="danger">{errorMessage(preview.error)}</Alert>}
            {p && !preview.isFetching && (
              <Stack gap={8}>
                <Row gap={24}>
                  <View>
                    <Text variant="small">Days</Text>
                    <Text variant="value">{p.days}</Text>
                  </View>
                  <View>
                    <Text variant="small">Balance</Text>
                    <Text variant="value">{p.balance}</Text>
                  </View>
                  <View>
                    <Text variant="small">After</Text>
                    <Text variant="value" color={p.balance_after < 0 ? colors.danger : undefined}>
                      {p.balance_after}
                    </Text>
                  </View>
                </Row>
                {p.warnings.length > 0 && <Alert tone="warning">{p.warnings}</Alert>}
                {p.errors.length > 0 && <Alert tone="danger">{p.errors}</Alert>}
              </Stack>
            )}
            <View style={{ alignItems: 'flex-end' }}>
              <Button title="Submit request" variant="primary" loading={apply.isPending} disabled={!valid || !!p?.errors.length} onPress={() => apply.mutate(undefined)} testID="leave-submit" />
            </View>
          </Stack>
        </Card>
        <Stack gap={10}>
          <Text variant="title">My leave requests</Text>
          <DataTable
            rows={requests.data}
            loading={requests.isLoading}
            onPageChange={setPage}
            onRowPress={(r) => r.workflow_instance_id && setWf(r.workflow_instance_id)}
            emptyTitle="No leave requests yet"
            columns={[
              {
                key: 'type',
                title: 'Type',
                width: 80,
                sortValue: (r) => r.leave_type?.code,
                render: (r) => (
                  <Row gap={6}>
                    <Dot color={r.leave_type?.color ?? colors.primary} />
                    <Text>{r.leave_type?.code}</Text>
                  </Row>
                ),
              },
              { key: 'from_date', title: 'Dates', width: 170, sortValue: (r) => r.from_date, render: (r) => (r.from_date === r.to_date ? fmtDate(r.from_date) : `${fmtDate(r.from_date)} – ${fmtDate(r.to_date)}`) },
              { key: 'days', title: 'Days', width: 60, align: 'right' },
              { key: 'status', title: 'Status', width: 100, sortValue: (r) => r.status, render: (r) => <StatusBadge status={r.status} /> },
              {
                key: 'act',
                title: '',
                width: 90,
                sortable: false,
                render: (r) => (r.status === 'pending' || (r.status === 'approved' && r.from_date > todayISO()) ? <Button size="sm" variant="ghost" title="Cancel" onPress={() => setCancelId(r.id)} /> : null),
              },
            ]}
          />
        </Stack>
      </Columns>
      <WorkflowSheet instanceId={wf} onClose={() => setWf(null)} />
      <ConfirmDialog open={!!cancelId} onClose={() => setCancelId(null)} onConfirm={() => cancelId && cancel.mutate(cancelId)} title="Cancel leave request?" message="Your balance will be restored." confirmLabel="Cancel leave" danger loading={cancel.isPending} />
    </Screen>
  );
}
